import Phaser from 'phaser';
import { APP_CONFIG } from '../../app/config';
import { SeededRandom } from '../../services/RandomSource';
import { drawPixelMoon } from './moonDesign';
import {
  generateStarChunk,
  StarChunkSeedSequence,
  type StarChunkConfig,
  type StarLayerId,
  type StarPoint,
} from './StarChunkGenerator';

type LayerConfig = Omit<StarChunkConfig, 'seed' | 'width' | 'height'> & {
  baseSeed: number;
  depth: number;
  speedFactor: number;
};

type StarChunk = {
  id: number;
  layer: StarLayerId;
  seed: number;
  view: Phaser.GameObjects.Graphics;
  stars: StarPoint[];
};

type BrightStar = {
  view: Phaser.GameObjects.Container;
  baseAlpha: number;
  amplitude: number;
  speed: number;
  offset: number;
};

export type SpaceBackgroundDebug = {
  layerCount: number;
  chunkCount: number;
  chunkSeeds: number[];
  generatedObjectCount: number;
  brightStarCount: number;
  visibleChunkCount: number;
  moonVisible: boolean;
  moonAlpha: number;
  moonDepth: number;
};

const CHUNK_WIDTH = APP_CONFIG.baseWidth;
const CHUNKS_PER_LAYER = 3;
const SESSION_SEED = 0x5a17c0de;

const LAYERS: readonly LayerConfig[] = [
  {
    layer: 'dust',
    baseSeed: SESSION_SEED + 10,
    depth: 1,
    speedFactor: 0.018,
    count: 62,
    sizeRange: { min: 1, max: 1 },
    alphaRange: { min: 0.18, max: 0.38 },
    colors: [0x34405f, 0x3a5368],
    minimumDistance: 3,
    crossChance: 0,
  },
  {
    layer: 'distant',
    baseSeed: SESSION_SEED + 100,
    depth: 2,
    speedFactor: 0.045,
    count: 36,
    sizeRange: { min: 1, max: 1 },
    alphaRange: { min: 0.4, max: 0.66 },
    colors: [0x8c91a7, 0xa5a79b, 0x668fa0],
    minimumDistance: 6,
    crossChance: 0,
  },
  {
    layer: 'regular',
    baseSeed: SESSION_SEED + 200,
    depth: 4,
    speedFactor: 0.11,
    count: 25,
    sizeRange: { min: 1, max: 2 },
    alphaRange: { min: 0.58, max: 0.9 },
    colors: [0xf6f3d4, 0xb8dfe5, 0xd9cdb6],
    minimumDistance: 10,
    crossChance: 0.04,
  },
  {
    layer: 'accent',
    baseSeed: SESSION_SEED + 300,
    depth: 5,
    speedFactor: 0.18,
    count: 6,
    sizeRange: { min: 2, max: 3 },
    alphaRange: { min: 0.72, max: 1 },
    colors: [0xfaf7dc, 0x9ee8ef, 0xffd7a3],
    minimumDistance: 52,
    crossChance: 0.55,
  },
] as const;

export class SpaceBackgroundController {
  private readonly chunks: StarChunk[] = [];
  private readonly chunksByLayer = new Map<StarLayerId, StarChunk[]>();
  private readonly brightStars: BrightStar[] = [];
  private readonly nextSeeds = new Map<StarLayerId, StarChunkSeedSequence>();
  private readonly moon: Phaser.GameObjects.Container;
  private elapsedMs = 0;

  public constructor(private readonly scene: Phaser.Scene) {
    this.moon = this.createMoon();
    this.createChunks();
    this.createBrightStars();
    this.reset();
  }

  public update(deltaMs: number, worldSpeed: number): void {
    const seconds = Math.min(deltaMs, 40) / 1000;
    this.elapsedMs += deltaMs;
    for (const layer of LAYERS) {
      const chunks = this.chunksByLayer.get(layer.layer) ?? [];
      for (const chunk of chunks) chunk.view.x -= worldSpeed * layer.speedFactor * seconds;
      for (const chunk of chunks) {
        if (chunk.view.x > -CHUNK_WIDTH) continue;
        let rightmost = -Infinity;
        for (const candidate of chunks) rightmost = Math.max(rightmost, candidate.view.x);
        chunk.view.x = rightmost + CHUNK_WIDTH;
        chunk.seed = this.takeNextSeed(layer.layer);
        this.redrawChunk(chunk, layer);
      }
    }
    for (const star of this.brightStars) {
      star.view.x -= worldSpeed * 0.16 * seconds;
      if (star.view.x < -8) star.view.x += APP_CONFIG.baseWidth + 28;
      star.view.alpha = Phaser.Math.Clamp(
        star.baseAlpha + Math.sin(this.elapsedMs * star.speed + star.offset) * star.amplitude,
        0.35,
        1,
      );
    }
    this.moon.x -= worldSpeed * 0.0015 * seconds;
  }

  public setIntroProgress(progress: number): void {
    this.moon.setPosition(350 - progress * 20, 62 + progress * 12);
  }

  public reset(): void {
    this.elapsedMs = 0;
    for (const layer of LAYERS) {
      this.nextSeeds.set(layer.layer, new StarChunkSeedSequence(layer.baseSeed + 1_000));
      const chunks = this.chunksByLayer.get(layer.layer) ?? [];
      chunks.forEach((chunk, index) => {
        chunk.seed = layer.baseSeed + index;
        chunk.view.setPosition(index * CHUNK_WIDTH, 0).setVisible(true);
        this.redrawChunk(chunk, layer);
      });
    }
    const random = new SeededRandom(SESSION_SEED + 900);
    for (const star of this.brightStars) {
      star.view.setPosition(
        Math.floor(random.next() * APP_CONFIG.baseWidth),
        12 + Math.floor(random.next() * 220),
      );
      star.offset = random.next() * Math.PI * 2;
      star.speed = 0.0012 + random.next() * 0.0025;
      star.baseAlpha = 0.68 + random.next() * 0.2;
      star.amplitude = 0.12 + random.next() * 0.18;
    }
    this.moon.setPosition(350, 62).setVisible(true).setAlpha(1).setScale(1);
  }

  public destroy(): void {
    this.chunks.forEach((chunk) => chunk.view.destroy());
    this.brightStars.forEach((star) => star.view.destroy(true));
    this.moon.destroy(true);
    this.chunks.length = 0;
    this.chunksByLayer.clear();
    this.brightStars.length = 0;
  }

  public get debug(): SpaceBackgroundDebug {
    return {
      layerCount: LAYERS.length,
      chunkCount: this.chunks.length,
      chunkSeeds: this.chunks.map((chunk) => chunk.seed),
      generatedObjectCount: this.chunks.length + this.brightStars.length + 1,
      brightStarCount: this.brightStars.length,
      visibleChunkCount: this.chunks.filter((chunk) => chunk.view.visible && chunk.view.alpha > 0)
        .length,
      moonVisible: this.moon.visible,
      moonAlpha: this.moon.alpha,
      moonDepth: this.moon.depth,
    };
  }

  private createChunks(): void {
    let id = 0;
    for (const layer of LAYERS) {
      const layerChunks: StarChunk[] = [];
      for (let index = 0; index < CHUNKS_PER_LAYER; index += 1) {
        const chunk: StarChunk = {
          id,
          layer: layer.layer,
          seed: layer.baseSeed + index,
          view: this.scene.add.graphics().setDepth(layer.depth),
          stars: [],
        };
        this.chunks.push(chunk);
        layerChunks.push(chunk);
        id += 1;
      }
      this.chunksByLayer.set(layer.layer, layerChunks);
    }
  }

  private redrawChunk(chunk: StarChunk, layer: LayerConfig): void {
    chunk.stars = generateStarChunk({
      ...layer,
      seed: chunk.seed,
      width: CHUNK_WIDTH,
      height: APP_CONFIG.baseHeight,
    });
    chunk.view.clear();
    for (const star of chunk.stars) {
      chunk.view.fillStyle(star.color, star.alpha).fillRect(star.x, star.y, star.size, star.size);
      if (star.cross) {
        chunk.view.fillRect(star.x - 1, star.y + 1, star.size + 2, 1);
        chunk.view.fillRect(star.x + 1, star.y - 1, 1, star.size + 2);
      }
    }
  }

  private createBrightStars(): void {
    for (let index = 0; index < 9; index += 1) {
      const horizontal = this.scene.add.rectangle(0, 0, 5, 1, 0xf6f3d4);
      const vertical = this.scene.add.rectangle(0, 0, 1, 5, index % 3 === 0 ? 0x9ee8ef : 0xf6f3d4);
      this.brightStars.push({
        view: this.scene.add.container(0, 0, [horizontal, vertical]).setDepth(6),
        baseAlpha: 0.8,
        amplitude: 0.15,
        speed: 0.002,
        offset: 0,
      });
    }
  }

  private createMoon(): Phaser.GameObjects.Container {
    const graphics = this.scene.add.graphics();
    drawPixelMoon(graphics);
    return this.scene.add.container(350, 62, [graphics]).setDepth(3);
  }

  private takeNextSeed(layer: StarLayerId): number {
    const sequence = this.nextSeeds.get(layer);
    return sequence?.take() ?? SESSION_SEED;
  }
}
